import { useQueryClient } from "@tanstack/react-query";
import { useNotify, useRecordContext, useTranslate, useUpdate } from "ra-core";
import { ExternalLink } from "lucide-react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { ClassRosterEntry } from "../types";

// Stages a roster row can move to. "lost" takes the player off the roster.
const ROW_STAGES = ["registered", "trial", "waitlist", "lost"] as const;

/** Change the enrollment's stage, or open it, straight from the roster. */
export const RosterRowActions = (_props: { label?: string }) => {
  const record = useRecordContext<ClassRosterEntry>();
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [update, { isPending }] = useUpdate();
  if (!record) return null;

  // A roster row's id is its enrollment (deal) id.
  const handleStageChange = (stage: string) => {
    if (stage === record.stage) return;
    update(
      "deals",
      { id: record.id, data: { stage }, previousData: { stage: record.stage } },
      {
        onSuccess: () => {
          notify("resources.classes.roster.stage_changed", {
            type: "info",
            messageArgs: {
              stage: translate(`resources.classes.roster.row_stages.${stage}`),
            },
          });
          // The roster sections and headcounts come from views: refetch just
          // those (a global refresh would blank the whole page while loading).
          queryClient.invalidateQueries({ queryKey: ["class_roster"] });
          queryClient.invalidateQueries({ queryKey: ["classes"] });
        },
        onError: (error) =>
          notify(
            typeof error === "string"
              ? error
              : (error as Error)?.message || "ra.notification.http_error",
            { type: "error" },
          ),
      },
    );
  };

  return (
    <div className="flex items-center gap-1">
      <Select
        value={record.stage}
        onValueChange={handleStageChange}
        disabled={isPending}
      >
        <SelectTrigger
          size="sm"
          className="w-36"
          aria-label={translate("resources.classes.roster.change_stage")}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ROW_STAGES.map((stage) => (
            <SelectItem key={stage} value={stage}>
              {translate(`resources.classes.roster.row_stages.${stage}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        variant="ghost"
        size="icon"
        asChild
        title={translate("resources.classes.roster.open_enrollment")}
      >
        <Link to={`/deals/${record.id}/show`}>
          <ExternalLink className="size-4" />
          <span className="sr-only">
            {translate("resources.classes.roster.open_enrollment")}
          </span>
        </Link>
      </Button>
    </div>
  );
};
